import React, { useEffect, useRef, useState } from "react";
import { FiRotateCcw, FiSquare } from "react-icons/fi";

const Hero = ({ language, t, scrollToSection }) => {
  const videoRef = useRef(null);
  const audioEnabledRef = useRef(false);
  const videoSrc =
    language === "es"
      ? "https://logibackup.sfo3.cdn.digitaloceanspaces.com/Videosweb/CargotronicsSpanish.mp4"
      : "https://logibackup.sfo3.cdn.digitaloceanspaces.com/Videosweb/CargotronicsEnglish.mp4";
  const [muted, setMuted] = useState(true);
  const [showOverlay, setShowOverlay] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    setMuted(true);
    setShowOverlay(true);
    video.load();
    const attempt = video.play();
    if (attempt && typeof attempt.catch === "function") {
      attempt.catch(() => { });
    }
  }, [videoSrc]);

  const handleEnableAudio = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    video.volume = 1;
    setMuted(false);
    audioEnabledRef.current = true;
    const attempt = video.play();
    if (attempt && typeof attempt.catch === "function") {
      attempt.catch(() => { });
    }
    setShowOverlay(false);
  };

  const handleVideoEnded = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    setMuted(true);
    setShowOverlay(true);
  };

  const handleVideoPause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.ended) return;
    video.muted = true;
    setMuted(true);
    setShowOverlay(true);
  };

  const handleReplay = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    audioEnabledRef.current = true;
    video.muted = false;
    video.volume = 1;
    setMuted(false);
    setShowOverlay(false);
    const attempt = video.play();
    if (attempt && typeof attempt.catch === "function") {
      attempt.catch(() => { });
    }
  };

  const handleStop = () => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.currentTime = 0;
    video.muted = true;
    setMuted(true);
    setShowOverlay(true);
    audioEnabledRef.current = false;
  };

  const goToSection = (hash) => {
    if (!hash || hash === "#") return;
    const id = hash.startsWith("#") ? hash.slice(1) : hash;
    scrollToSection(id);
  };

  return (
    <section className="hero dark-gradient-section anim-section home-section" id="hero">
      <div className="hero-media" aria-hidden="true">
        <img
          className="hero-bg-image"
          src={`${process.env.PUBLIC_URL}/heroimage.jpeg`}
          alt=""
        />
      </div>
      <div className="home-container hero-grid">
        <div className="hero-copy">
          <div className="hero-badge">
            <span className="hero-badge-dot" aria-hidden="true" />
            <span>{t.hero.badge}</span>
          </div>
          <h1>{t.hero.title}</h1>
          <p className="hero-sub">
            {t.hero.subtitle}
          </p>
          <div className="hero-cta">
            <button className="btn btn-primary" type="button" onClick={() => goToSection("#contacto")}>
              {t.buttons.solicitarDemo}
            </button>
            <button className="btn btn-outline" type="button" onClick={() => goToSection("#integraciones")}>
              {t.hero.actions.integrator}
            </button>
            <button className="btn btn-ghost" type="button" onClick={() => goToSection("#producto")}>
              {t.hero.actions.howItWorks}
            </button>
          </div>
          <div className="hero-tags">
            {t.hero.tags.map((tag) => (
              <span className="hero-tag" key={tag.label}>
                <span className="hero-tag-emoji" aria-hidden="true">
                  {tag.icon}
                </span>
                {tag.label}
              </span>
            ))}
          </div>
        </div>
        <div className="hero-visual">
          <div className="mockup float anim-visual">
            <div className="mockup-map mockup-video">
              <video
                ref={videoRef}
                className="mockup-video-el"
                autoPlay
                playsInline
                muted={muted}
                preload="auto"
                aria-hidden="true"
                onEnded={handleVideoEnded}
                onPause={handleVideoPause}
              >
                <source src={videoSrc} type="video/mp4" />
              </video>
              {showOverlay && (
                <button
                  className="video-overlay"
                  type="button"
                  onClick={handleEnableAudio}
                  aria-label={language === "es" ? "Ver video con audio" : "Watch video with audio"}
                >
                  {language === "es" ? "Ver video" : "Watch video"}
                </button>
              )}
            </div>
            <div className="video-controls">
              <button
                type="button"
                className="video-control"
                onClick={handleReplay}
                aria-label={language === "es" ? "Repetir" : "Replay"}
                title={language === "es" ? "Repetir" : "Replay"}
              >
                <FiRotateCcw className="video-control-icon" aria-hidden="true" />
                <span className="visually-hidden">
                  {language === "es" ? "Repetir" : "Replay"}
                </span>
              </button>
              <button
                type="button"
                className="video-control"
                onClick={handleStop}
                aria-label={language === "es" ? "Detener" : "Stop"}
                title={language === "es" ? "Detener" : "Stop"}
              >
                <FiSquare className="video-control-icon" aria-hidden="true" />
                <span className="visually-hidden">
                  {language === "es" ? "Detener" : "Stop"}
                </span>
              </button>
            </div>
            <div className="mockup-panels">
              {/* <div className="mockup-card">
                <h5>{language === "es" ? "Alertas" : "Alerts"}</h5>
                <ul>
                  <li>{language === "es" ? "Desvío detectado" : "Deviation detected"}</li>
                  <li>{language === "es" ? "Parada prolongada" : "Extended stop"}</li>
                  <li>{language === "es" ? "Puerta abierta" : "Door open"}</li>
                </ul>
              </div> */}
              <div className="mockup-card">
                <h5>{language === "es" ? "Cargotronics en 90 segundos" : "Cargotronics in 90 seconds"}</h5>
                <div className="report-row">
                  <span>{language === "es" ? "Qué hacemos, para quién y por qué es diferente." : "What we do, for whom, and why is different."}</span>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
